/**
 * Drop-in replacement for @mui/styles/makeStyles — Vite + MUI v5 compatible.
 * Uses a singleton emotion cache (key: 'css') so class names co-exist with MUI.
 */
import { useTheme } from '@mui/material/styles';
import createCache from '@emotion/cache';
import { serializeStyles } from '@emotion/serialize';
import { insertStyles } from '@emotion/utils';

type StylesCreator = (theme: any, props?: any) => Record<string, any>;
type StylesOrCreator = Record<string, any> | StylesCreator;

// Singleton cache — same key as MUI's default, so specificity stays consistent
let _cache: ReturnType<typeof createCache> | null = null;
function getCache() {
    if (!_cache) {
        _cache = createCache({ key: 'css' });
    }
    return _cache;
}

function makeStyles(stylesOrCreator: StylesOrCreator) {
    return (props?: Record<string, any>) => {
        const theme = useTheme() as any;
        const cache = getCache();

        const rawStyles =
            typeof stylesOrCreator === 'function'
                ? stylesOrCreator(theme, props || {})
                : stylesOrCreator;

        const classes: Record<string, string> = {};
        for (const [key, value] of Object.entries(rawStyles)) {
            const styleObj = typeof value === 'function' ? value(props || {}) : value;
            if (!styleObj || typeof styleObj !== 'object') continue;

            const serialized = serializeStyles([styleObj], cache.registered);
            insertStyles(cache, serialized, true);
            classes[key] = `${cache.key}-${serialized.name}`;
        }
        return classes;
    };
}

export default makeStyles;
